import React, { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApp, useSyncOnFocus } from "../context/AppContext";
import { JOB_STEPS } from "../data/stages";
import CompanyLogo from "../components/CompanyLogo";
import StageSelect from "../components/StageSelect";
import Modal from "../components/Modal";
import EmptyState from "../components/EmptyState";
import { awaitingReview, isStepSkipped, nextStep, stepProgress } from "../utils/jobs";
import PlatypusBanner, { AppliedBanner } from "./job/PlatypusBanner";
import OverviewTab from "./job/OverviewTab";
import ResumeTab from "./job/ResumeTab";
import CoverLetterTab from "./job/CoverLetterTab";
import ApplyTab from "./job/ApplyTab";
import ContactsTab from "./job/ContactsTab";
import EventsTab from "./job/EventsTab";

const RECENT_MS = 2 * 60 * 60 * 1000;

const TABS = [
  { id: "overview", label: "Overview", icon: "bi-info-circle" },
  { id: "resume", label: "Resume", icon: "bi-file-earmark-person", step: "resume" },
  { id: "cover-letter", label: "Cover letter", icon: "bi-envelope-paper", step: "coverLetter" },
  { id: "apply", label: "Apply", icon: "bi-magic", step: "applied" },
  { id: "contacts", label: "Contacts", icon: "bi-person-lines-fill", step: "emailed" },
  { id: "events", label: "Events", icon: "bi-calendar-event" },
];

function Checklist({ job, onGo }) {
  const { done, total } = stepProgress(job);
  const next = nextStep(job);
  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h6 mb-0">Your steps</h2>
          <span className="small text-muted">
            {done} of {total} done
          </span>
        </div>
        <div className="progress mb-3" style={{ height: 6 }}>
          <div className="progress-bar bg-success" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <div className="d-flex flex-column gap-1">
          {JOB_STEPS.map((step, i) => {
            const isDone = job.steps?.[step.id];
            const isNext = next?.id === step.id;
            const skipped = isStepSkipped(job, step);
            return (
              <button
                key={step.id}
                className={`step-item ${isDone ? "done" : ""} ${isNext ? "next" : ""} ${skipped ? "skipped" : ""}`}
                onClick={() => onGo(step.tab)}
              >
                <span className="step-num">
                  {isDone ? <i className="bi bi-check-lg" /> : skipped ? <i className="bi bi-dash-lg" /> : i + 1}
                </span>
                <span className="flex-grow-1 text-start">{step.label}</span>
                {isNext && <span className="badge text-bg-primary">Next</span>}
                {skipped && <span className="small text-muted">Not required</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function JobDetail() {
  const { id } = useParams();
  const { jobs, deleteJob, toast } = useApp();
  useSyncOnFocus();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const job = jobs.find((j) => j.id === id);
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "overview";

  if (!job) {
    return (
      <div className="card mt-4">
        <EmptyState
          icon="bi-question-circle"
          title="Job not found"
          text="It may have been deleted."
          action={
            <Link to="/tracker" className="btn btn-primary btn-sm">
              Back to tracker
            </Link>
          }
        />
      </div>
    );
  }

  const goTo = (tabId) => {
    setParams(tabId === "overview" ? {} : { tab: tabId });
    document.getElementById("job-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const backTo = awaitingReview(job) ? { to: "/platypus", label: "Platypus" } : { to: "/tracker", label: "Tracker" };
  const next = nextStep(job);
  const justApplied = !!job.automator && job.stage === "applied" && Date.now() - new Date(job.appliedAt).getTime() < RECENT_MS;

  return (
    <>
      <Link to={backTo.to} className="back-link">
        <i className="bi bi-arrow-left" /> {backTo.label}
      </Link>

      <div className="card mb-3">
        <div className="card-body d-flex flex-column flex-md-row gap-3 align-items-md-center">
          <div className="d-flex gap-3 align-items-center flex-grow-1 min-w-0">
            <CompanyLogo name={job.company} size={56} />
            <div className="min-w-0">
              <h1 className="h4 mb-0">{job.title}</h1>
              <div className="text-muted">
                {job.company} · {job.location}
              </div>
              <div className="d-flex flex-wrap gap-2 mt-1 small text-muted">
                <span>
                  <i className="bi bi-briefcase me-1" />
                  {job.workType}
                </span>
                {job.salary && (
                  <span>
                    <i className="bi bi-cash me-1" />
                    {job.salary}
                  </span>
                )}
                {job.url && (
                  <a href={job.url} target="_blank" rel="noreferrer">
                    <i className="bi bi-box-arrow-up-right me-1" />
                    View posting
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center flex-shrink-0">
            <div style={{ minWidth: 150 }}>
              <label className="form-label small text-muted mb-1 d-block">Stage</label>
              <StageSelect job={job} size="md" />
            </div>
            <div className="dropdown align-self-end">
              <button className="btn btn-light" data-bs-toggle="dropdown" aria-label="More actions">
                <i className="bi bi-three-dots" />
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button className="dropdown-item text-danger" onClick={() => setConfirmDelete(true)}>
                    <i className="bi bi-trash me-2" />
                    Delete job
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {awaitingReview(job) && <PlatypusBanner job={job} onGo={goTo} />}
      {justApplied && <AppliedBanner job={job} />}

      {next && !awaitingReview(job) && (
        <div className="next-banner d-lg-none mb-3">
          <div className="small">
            <div className="text-muted">Next step</div>
            <div className="fw-medium">{next.label}</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => goTo(next.tab)}>
            Go <i className="bi bi-arrow-right" />
          </button>
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-8">
          <div id="job-tabs" className="tab-scroll mb-3">
            {TABS.map((t) => (
              <button key={t.id} className={`tab-btn ${tab === t.id ? "active" : ""}`} onClick={() => goTo(t.id)}>
                <i className={`bi ${t.icon}`} />
                {t.label}
                {t.step && job.steps?.[t.step] && <i className="bi bi-check-circle-fill text-success small" />}
              </button>
            ))}
          </div>

          {tab === "overview" && <OverviewTab job={job} />}
          {tab === "resume" && <ResumeTab job={job} onNext={() => goTo(job.noCoverLetter ? "apply" : "cover-letter")} />}
          {tab === "cover-letter" && <CoverLetterTab job={job} onNext={() => goTo("apply")} />}
          {tab === "apply" && <ApplyTab job={job} onNext={() => goTo("contacts")} />}
          {tab === "contacts" && <ContactsTab job={job} />}
          {tab === "events" && <EventsTab job={job} />}
        </div>
        <div className="col-lg-4 d-none d-lg-block">
          <div className="sticky-side">
            <Checklist job={job} onGo={goTo} />
          </div>
        </div>
      </div>

      <Modal
        show={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this job?"
        footer={
          <>
            <button className="btn btn-light" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={async () => {
                const { title, company } = job;
                navigate("/tracker");
                if (await deleteJob(job.id)) toast(`Deleted ${title} at ${company}`, "info");
              }}
            >
              Delete
            </button>
          </>
        }
      >
        <p className="mb-0">
          This removes <strong>{job.title}</strong> at <strong>{job.company}</strong> and everything you've prepared for it.
          This can't be undone. If you just didn't get it, move it to <strong>Rejected</strong> instead.
        </p>
      </Modal>
    </>
  );
}

export default JobDetail;
